import type { SourceType } from '../../types'

export const MAX_IMPORT_FILE_BYTES = 25 * 1024 * 1024
export const MAX_SHEET_DATA_ROWS = 30_000
export const HEADER_SCAN_NON_EMPTY_ROWS = 30
export const DATA_PREVIEW_ROWS = 10

export type ImportSourceType = SourceType

export type SupportedImportFormat = 'xlsx' | 'csv'

export type ImportCellScalar = string | number | boolean | null

export type ImportCellKind = 'blank' | 'text' | 'number' | 'boolean' | 'date' | 'formula' | 'error'

export type FormulaResultState = 'cached' | 'missing' | 'error'

/**
 * A structured-clone-safe representation of an Excel/CSV cell.
 *
 * `displayText` is intentionally kept separately from `rawValue`: item codes
 * should use the displayed text so a numeric cell formatted as `000000` keeps
 * its leading zeroes, while quantities and prices should use `rawValue`.
 */
export interface ImportedCell {
  rawValue: ImportCellScalar
  displayText: string
  kind: ImportCellKind
  numberFormat?: string
  formula?: string
  formulaResultState?: FormulaResultState
}

export interface ImportedRow {
  sourceRowNumber: number
  cells: ImportedCell[]
}

export interface SheetScanRow extends ImportedRow {
  nonEmptyCellCount: number
}

export interface ImportedSheetMetadata {
  name: string
  index: number
  visibility: 'visible' | 'hidden' | 'veryHidden'
  declaredRowCount: number
  declaredColumnCount: number
  nonEmptyRowCount: number
  firstNonEmptyRowNumber: number | null
  lastNonEmptyRowNumber: number | null
  potentiallyExceedsDataRowLimit: boolean
  scanRows: SheetScanRow[]
}

export interface CsvDialectMetadata {
  delimiter: string
  linebreak: string
  warnings: string[]
}

export interface ImportedWorkbookMetadata {
  workbookId: string
  sourceType: ImportSourceType
  fileName: string
  fileSize: number
  format: SupportedImportFormat
  sheetCount: number
  sheets: ImportedSheetMetadata[]
  csvDialect?: CsvDialectMetadata
  warnings: string[]
}

export interface ParsedSheet {
  workbookId: string
  sourceType: ImportSourceType
  fileName: string
  fileSize: number
  format: SupportedImportFormat
  sheet: ImportedSheetMetadata
  headerRow: number
  headerCells: ImportedCell[]
  rows: ImportedRow[]
  previewRows: ImportedRow[]
  effectiveDataRowCount: number
  warnings: string[]
}

export type ImportErrorCode =
  | 'UNSUPPORTED_FILE_TYPE'
  | 'FILE_TOO_LARGE'
  | 'EMPTY_FILE'
  | 'INVALID_FILE'
  | 'CSV_PARSE_ERROR'
  | 'WORKBOOK_NOT_FOUND'
  | 'SHEET_NOT_FOUND'
  | 'SHEET_ROW_LIMIT_EXCEEDED'
  | 'WORKER_UNAVAILABLE'
  | 'WORKER_FAILURE'
  | 'REQUEST_CANCELLED'

export interface ImportErrorPayload {
  code: ImportErrorCode
  message: string
  details?: Record<string, string | number | boolean | null>
}

export class BoqImportError extends Error {
  readonly code: ImportErrorCode
  readonly details?: Record<string, string | number | boolean | null>

  constructor(payload: ImportErrorPayload) {
    super(payload.message)
    this.name = 'BoqImportError'
    this.code = payload.code
    this.details = payload.details
  }

  toPayload(): ImportErrorPayload {
    return {
      code: this.code,
      message: this.message,
      ...(this.details ? { details: this.details } : {}),
    }
  }
}

export interface ImportFileInput {
  name: string
  size: number
  type?: string
  lastModified?: number
  arrayBuffer(): Promise<ArrayBuffer>
}

export interface ParseSheetOptions {
  /** One-based source row number, matching Excel's row numbering. */
  headerRow: number
}

export type ImportProgressStage =
  | 'validating'
  | 'reading-file'
  | 'opening-workbook'
  | 'scanning-sheets'
  | 'reading-sheet'
  | 'complete'

export interface ImportProgress {
  requestId: string
  stage: ImportProgressStage
  progress: number
  message: string
}

export function detectImportFormat(fileName: string): SupportedImportFormat {
  const normalizedName = fileName.trim().toLocaleLowerCase()
  if (normalizedName.endsWith('.xlsx')) return 'xlsx'
  if (normalizedName.endsWith('.csv')) return 'csv'

  const isLegacyXls = normalizedName.endsWith('.xls')
  throw new BoqImportError({
    code: 'UNSUPPORTED_FILE_TYPE',
    message: isLegacyXls
      ? '暂不支持旧版 .xls 文件，请先使用 Excel 或 WPS 另存为 .xlsx。'
      : '仅支持 .xlsx 和 .csv 文件。',
    details: { fileName },
  })
}

export function assertImportFileSize(fileName: string, size: number): void {
  if (size <= 0) {
    throw new BoqImportError({
      code: 'EMPTY_FILE',
      message: '文件为空，无法读取清单数据。',
      details: { fileName, size },
    })
  }
  if (size > MAX_IMPORT_FILE_BYTES) {
    throw new BoqImportError({
      code: 'FILE_TOO_LARGE',
      message: `文件超过 25 MB 限制，请精简工作簿后重试。`,
      details: { fileName, size, maxBytes: MAX_IMPORT_FILE_BYTES },
    })
  }
}

export function importedRowToDisplayValues(row: ImportedRow): string[] {
  return row.cells.map((cell) => cell.displayText)
}
